import { Fragment } from "react";
import type { Assignment, Point } from "@cs2/contracts";
import type Konva from "konva";
import type { WalkingPathResult } from "@cs2/physics";
import {
  BlockedMarker,
  RouteLine,
  StepMarker,
  TeamPlayerBadge,
} from "./konva-marks.js";
import { RouteActionMarkers } from "./route-actions.js";

type WalkPaths = Map<string, WalkingPathResult>;

/** Routenlinien aller Zuweisungen außer der ausgeschlossenen (eigenen). */
export function TeamRouteLines({
  assignments,
  excludeSlot,
  walkPaths,
  sx,
  sy,
  scale,
}: {
  assignments: readonly Assignment[];
  excludeSlot: string;
  walkPaths: WalkPaths;
  sx: number;
  sy: number;
  scale: number;
}) {
  return (
    <Fragment>
      {assignments
        .filter((assignment) => assignment.slot !== excludeSlot)
        .map((assignment) => {
          const path = walkPaths.get(assignment.slot);
          return (
            <RouteLine
              key={assignment.slot}
              variant="team"
              points={path?.points ?? assignment.route}
              {...(path ? { covered: path.covered } : {})}
              sx={sx}
              sy={sy}
              scale={scale}
            />
          );
        })}
    </Fragment>
  );
}

/** Badges am Routenende jeder Zuweisung außer der ausgeschlossenen (eigenen). */
export function TeamBadges({
  assignments,
  excludeSlot,
  sx,
  sy,
  scale,
}: {
  assignments: readonly Assignment[];
  excludeSlot: string;
  sx: number;
  sy: number;
  scale: number;
}) {
  return (
    <Fragment>
      {assignments
        .filter(
          (assignment) =>
            assignment.slot !== excludeSlot && assignment.route.length > 0,
        )
        .map((assignment) => {
          const end = assignment.route.at(-1)!;
          return (
            <TeamPlayerBadge
              key={assignment.slot}
              slot={assignment.slot}
              x={end.x * sx}
              y={end.y * sy}
              scale={scale}
            />
          );
        })}
    </Fragment>
  );
}

/**
 * Eigene Route mit Aktionsmarkern (Sprung, Drop, Leiter) und
 * „Route verlässt die begehbare Fläche"-Marker.
 */
export function OwnRoute({
  path,
  blockedPoint,
  sx,
  sy,
  scale,
}: {
  path: WalkingPathResult;
  blockedPoint: Point | null;
  sx: number;
  sy: number;
  scale: number;
}) {
  const { blockedAt } = path;
  return (
    <Fragment>
      <RouteLine
        variant="own"
        points={path.points}
        covered={path.covered}
        sx={sx}
        sy={sy}
        scale={scale}
      />
      <RouteActionMarkers
        points={path.points}
        actions={path.actions}
        sx={sx}
        sy={sy}
        scale={scale}
      />
      {blockedAt !== null && blockedPoint ? (
        <BlockedMarker
          x={blockedPoint.x * sx}
          y={blockedPoint.y * sy}
          scale={scale}
        />
      ) : null}
    </Fragment>
  );
}

/**
 * Nummerierte Routenpunkte. Ohne `onStepClick`/`onStepDragEnd` (Quickview)
 * rein darstellend — keine Auswahl, kein Abdunkeln, kein Ziehen. Mit
 * Handlern (Editor) trägt jeder Punkt Auswahlzustand und Drag/Klick; die
 * Validierung eines gezogenen Punkts bleibt beim Aufrufer, der Handler
 * bekommt das rohe Konva-Event.
 */
export function RouteStepMarkers({
  route,
  numberedCount,
  sx,
  sy,
  scale,
  selectedStepOrder = null,
  onStepClick,
  onStepDragEnd,
}: {
  route: readonly Point[];
  numberedCount: number;
  sx: number;
  sy: number;
  scale: number;
  selectedStepOrder?: number | null;
  onStepClick?: (stepOrder: number) => void;
  onStepDragEnd?: (
    index: number,
    event: Konva.KonvaEventObject<DragEvent>,
  ) => void;
}) {
  const interactive = Boolean(onStepClick || onStepDragEnd);
  return (
    <Fragment>
      {route.map((point, index) => {
        const isNumbered = index < numberedCount;
        const stepOrder = index + 1;
        const isSelectedStep =
          interactive && isNumbered && stepOrder === selectedStepOrder;
        const isDimmed =
          interactive && selectedStepOrder !== null && !isSelectedStep;
        return (
          <StepMarker
            key={`${point.x}-${point.y}-${index}`}
            x={point.x * sx}
            y={point.y * sy}
            scale={scale}
            isNumbered={isNumbered}
            stepNumber={index + 1}
            {...(interactive
              ? {
                  selected: isSelectedStep,
                  opacity: isDimmed ? 0.4 : 1,
                  interactive: true as const,
                  onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) =>
                    onStepDragEnd?.(index, event),
                  onClick: (event: Konva.KonvaEventObject<MouseEvent>) => {
                    if (!isNumbered) return;
                    event.cancelBubble = true;
                    onStepClick?.(stepOrder);
                  },
                  onTap: (event: Konva.KonvaEventObject<Event>) => {
                    if (!isNumbered) return;
                    event.cancelBubble = true;
                    onStepClick?.(stepOrder);
                  },
                }
              : {})}
          />
        );
      })}
    </Fragment>
  );
}
